<template>
  <div class="history">
    <navigation-element
      :list="navigation"
      @navigationSelectedItem = "navigationSelectedItem"
    />

    <div class="history__body">
      <div class="history__header">
        <div class="g-title history__header__title">
          {{ computedTitle }}
        </div>
        <ol class="history__header__option-buttons">
          <button
            class="g-btn g-btn--rounded history__header__option-buttons__refresh"
            title="Export to CSV"
            @click="currentTableToCSV"
          >
            <font-awesome-icon :icon="['fas', 'file-csv']" />
          </button>
          <li v-if="selectedItem">
            <button
              v-for="option in navigation.find(item => item.value === selectedItem).options"
              :key="option.value"
              class="g-btn g-btn--rounded"
              :class="{'g-btn--blue': option === optionSelected}"
              @click.stop="optionSelected = option"
            >
              {{ option.label }}
            </button>

            <button
              class="g-btn g-btn--rounded history__header__option-buttons__refresh"
              title="Refresh data"
              @click.stop="requestData()"
            >
              <font-awesome-icon :icon="['fas', 'sync']" />
            </button>

            <button
              class="g-btn g-btn--rounded history__header__option-buttons__refresh"
              title="Reverse sort"
              @click.stop="reverse = !reverse"
            >
              <font-awesome-icon :icon="['fas', reverse ? 'sort-amount-up' : 'sort-amount-down']" />
            </button>
          </li>
        </ol>
      </div>

      <grid-element
        v-if="selectedItem"
        ref="grid"
        :list="list"
        :format-table="formatTables[selectedItem]"
        :reverse="reverse"
      />
    </div>
  </div>
</template>

<script>
import {SocketHandler, LifeCycle, MicroService, UnitSystem} from '@/assets/javascript/mixins/Mixins'
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome'
import NavigationElement from '@/components/elements/NavigationElement'
import GridElement from '@/components/elements/GridElement'
import moment from 'moment'
import $ from'jquery'
import {secondsToObj} from '@/assets/javascript/Time-lib'

export default {
  name: 'History',
  components: {
    'font-awesome-icon': FontAwesomeIcon,
    'navigation-element': NavigationElement,
    'grid-element': GridElement
  },
  mixins: [SocketHandler, LifeCycle, MicroService, UnitSystem],
  props: {
    store: {
      type: Object,
      default() {
        return {}
      }
    }
  },
  data() {
    return {
      reverse: false,
      optionSelected: null,
      selectedItem: null,
      navigation: [],
      list: null,

      formatTables: {
        measures: {
          date: {
            label: this.$t('messages.sharedTexts.time.date'),
            valueConversion: this.dateFromEpoch
          },
          time: {
            label: this.$t('messages.settings.time'),
            valueConversion: this.timeFromEpoch
          },
          name: {
            label: this.$t('messages.history.name'),
            valueConversion: (value) => {
              if(value === '') return '-'
              return value
            }
          },
          description: {
            label: this.$t('messages.history.description'),
            valueConversion: (value) => {
              if(value === '') return '-'
              return value          
            }
          },
          type: {
            label: this.$t('messages.history.type'),
            valueConversion: (value) => {
              if(value === '') return '-'
              return value
              // might be needed later!  -jimm 07.april.2021
              // return this.translationOrValue(value, {
              //   M: 'messages.header.manual',
              //   A: 'messages.history.stopReasons.alarm',
              // })
            }
          },
          value: {
            label: this.$t('messages.sharedTexts.value'),
            valueConversion: (value) => {
              if(value === '') return '-'
              return value
            }           
          },
          units: {
            label: this.$t('messages.history.units'),
            valueConversion: (value) => {
              if(value === '') return '-'
              return value   
            }       
          }
        },
        water: {
          year: {
            label: this.$tc('messages.sharedTexts.time.year', 1),
            valueConversion: null
          },
          month: {
            label: this.$tc('messages.sharedTexts.time.month', 1),
            valueConversion: (value) => value < 13
              ? this.$t(`messages.sharedTexts.time.months.${value}`)
              : `<b>${this.$t('messages.history.yearlyTotal')}</b>`
          },
          day: {
            label: this.$tc('messages.sharedTexts.time.day', 1),
            valueConversion: (value) => value < 32
            ? value
            : `<b>Monthly Totals</b>`   // needs translation(s)
          },
          hh: {
            label: this.$tc('messages.sharedTexts.time.hour', 1),
            valueConversion: (value) => value < 24
            ? value
            : `<b>Daily Totals</b>` 
          },
          galHour: {
            label: this.$t('messages.waterConsumption.irrigated') + ' ' + this.getDenominator('litre', 'gallon', 2),
            valueConversion: this.convertGal
          },
          galDay: {
            label: this.$t('messages.waterConsumption.irrigated') + ' ' + this.getDenominator('litre', 'gallon', 2),
            valueConversion: this.convertGal
          },
          galMonth: {
            label: this.$t('messages.waterConsumption.irrigated')  + ' ' + this.getDenominator('litre', 'gallon', 2),
            valueConversion: this.convertGal
          },
          expHour: {
            label: this.$t('messages.waterConsumption.expected') + ' ' + this.getDenominator('litre', 'gallon', 2),
            valueConversion: this.convertGal
          },
          expDay: {
            label: this.$t('messages.waterConsumption.expected') + ' ' + this.getDenominator('litre', 'gallon', 2),
            valueConversion: this.convertGal
          },
          expMonth: {
            label: this.$tc('messages.waterConsumption.expected', 1) + ' ' + this.getDenominator('litre', 'gallon', 2),
            valueConversion: this.convertGal
          },
          unschedHour: {
            label: this.$t('messages.waterConsumption.unscheduled') + ' ' + this.getDenominator('litre', 'gallon', 2),
            valueConversion: this.convertGal
          },
          unschedDay: {
            label: this.$t('messages.waterConsumption.unscheduled') + ' ' + this.getDenominator('litre', 'gallon', 2),
            valueConversion: this.convertGal
          },
          unschedMonth: {
            label: this.$t('messages.waterConsumption.unscheduled')  + ' ' + this.getDenominator('litre', 'gallon', 2),
            valueConversion: this.convertGal
          }
        },
        intelliset: {
          year: {
            label: this.$tc('messages.sharedTexts.time.year', 1),
            valueConversion: null
          },
          month: {
            label: this.$tc('messages.sharedTexts.time.month', 1),
            valueConversion: (value) => {
              if(value === '-1') return '-'
              
              return value < 13
              ? this.$t(`messages.sharedTexts.time.months.${value}`)
              : `<b>${this.$t('messages.history.yearlyTotal')}</b>`
            }
          },
          day: {
            label: this.$tc('messages.sharedTexts.time.day', 1),
            // valueConversion: this.valueOrDash
            valueConversion: (value) => {
              if(value === '-1') return '-'
              
              return value < 32 ? value :  `<b>Monthly Totals</b>`
            }
          },
          hour: {
            label: this.$tc('messages.sharedTexts.time.hour', 1),
            // valueConversion: this.valueOrDash
            valueConversion: (value) => {
              if(value === '-1') return '-'
              
              return value < 24 ? value : `<b>Daily Totals</b>` 
            }
          },
          et: {
            label: this.$t('messages.history.et'),
            valueConversion: this.convertGal
          },
          etc: {
            label: this.$t('messages.history.etc'),
            valueConversion: this.convertGal
          },
          etCount: {
            label: this.$t('messages.history.etCount'),
            valueConversion: this.convertGalFloat
          },
          etcCount: {
            label: this.$t('messages.history.etcCount'),
            valueConversion: this.convertGalFloat
          },
          actualRain: {
            label: this.$t('messages.history.actualRain'),
            valueConversion: this.convertGal
          },
          actualRainHour: {
            label: this.$t('messages.history.actualRainHour'),
            valueConversion: this.convertGal
          },
          usableRain: {
            label: this.$t('messages.history.usableRain'),
            valueConversion: this.convertGal
          },
          usableRainHour: {
            label: this.$t('messages.history.usableRainHour'),
            valueConversion: this.convertGal
          },
          usableRainCount: {
            label: this.$t('messages.history.usableRainCount'),
            valueConversion: this.convertGal
          },
          rainHourDiscard: {
            label: this.$t('messages.history.rainHourDiscard'),
            valueConversion: this.convertGal
          },
          excessiveRainCount: {
            label: this.$t('messages.history.carryOverRainCount'),
            valueConversion: this.convertGal
          },
        },
        program: {
          date: {
            label: this.$t('messages.sharedTexts.time.date'),
            valueConversion: this.dateFromEpoch
          },
          program: {
            label: this.$t('messages.history.navigation.program'),
            valueConversion: this.valueOrDash
          },
          start: {
            label: this.$t('messages.history.startTime'),
            valueConversion: this.timeFromEpoch
          },
          stop: {
            label: this.$t('messages.history.stopTime'),
            valueConversion: this.timeFromEpoch
          },
          runtime: {
            label: this.$t('messages.programEdit.setRunTimes.runtime'),
            valueConversion: (value) => this.secondsToTimeString(value)
          },
          gallons: {
            label: this.$t('messages.history.waterUsage') + ' ' + this.$t('messages.history.in') + ' ' + this.getDenominator('litre', 'gallon', 2),
            valueConversion: this.convertGal
          },
          station: {
            label: this.$tc('messages.sharedTexts.station', 1),
            valueConversion: (value) => {
              if(value === '-1') return '-'
              if(value < 10000) return value
              else return {'10001': 'MV', '11001': 'Boo1', '11002': 'Boo2', '12001': 'COV'}[value]
            }
          },
          status: {
            label: this.$t('messages.sharedTexts.status.status'),
            valueConversion: (value) => {
              if(value === '-1') return '-'
              return this.translationOrValue(value, {
                '0': 'messages.history.statusTypes.stopped',
                '1': 'messages.history.statusTypes.running',
                '3': 'messages.history.statusTypes.paused',
                '5': 'messages.history.statusTypes.waiting',
                '6': 'messages.history.statusTypes.soaking'
              })
            }
          },
          stopReason: {
            label: this.$t('messages.history.stopReason'),
            valueConversion: (value) => this.translationOrValue(value, {
              '0': 'messages.history.stopReasons.ok',
              '1': 'messages.history.stopReasons.fail',
              '2': 'messages.history.stopReasons.stoppedByLocalOperator',
              '3': 'messages.history.stopReasons.stoppedByRemoteOperator',
              '4': 'messages.history.stopReasons.maxPrograms',
              '5': 'messages.history.stopReasons.maxStations',
              '6': 'messages.history.stopReasons.nonWW',
              '7': 'messages.history.stopReasons.alarm',
              '8': 'messages.history.stopReasons.modeChange',
              '9': 'messages.history.stopReasons.cyclesNotEnded'
            })
          }
        },
        errorandstatus: {
          seqno: {
            label: 'seqno',
              valueConversion: null
          },
          c1: {
            label: this.$t('messages.history.value1'),
            valueConversion: (value) => {
              if(value > 946684800) return moment.utc(value, 'X')
              if(value === '') return '-'

              return this.translationOrValue(value, {
                MI: 'messages.header.irroff',
                MM: 'messages.header.manual',
                MA: 'messages.header.auto',
                MP: 'messages.header.program',
                MS: 'messages.header.setup',
                MV: 'messages.header.advanced',
                A1: 'messages.sharedTexts.alarms.1',
                A2: 'messages.sharedTexts.alarms.2',
                A3: 'messages.sharedTexts.alarms.3',
                A4: 'messages.sharedTexts.alarms.4',
                A5: 'messages.sharedTexts.alarms.5',
                A6: 'messages.sharedTexts.alarms.6',
                A7: 'messages.sharedTexts.alarms.7',
                A8: 'messages.sharedTexts.alarms.8',
                A9: 'messages.sharedTexts.alarms.9',
                A10: 'messages.sharedTexts.alarms.10',
                A11: 'messages.sharedTexts.alarms.11',
                A12: 'messages.sharedTexts.alarms.12',
                A13: 'messages.sharedTexts.alarms.13',
                A14: 'messages.sharedTexts.alarms.14',
                A15: 'messages.sharedTexts.alarms.15',
                I0: 'messages.history.local',
                I1: 'messages.history.remote',
                G0: 'messages.history.local',
                G1: 'messages.history.remote',
                Z1: 'messages.sharedTexts.status.on',
                W0: 'messages.sharedTexts.controls.end',
                W1: 'messages.sharedTexts.controls.begin',
              })
            }
          },
          date: {
            label: this.$t('messages.sharedTexts.time.date'),
            valueConversion: this.dateFromEpoch
          },
          time: {
            label: this.$t('messages.settings.time'),
            valueConversion: this.timeFromEpoch
          },
          type: {
            label: this.$t('messages.history.type'),
            valueConversion: (value) => {
              if(value === '') return '-'
              
              return this.translationOrValue(value, {
                M: 'messages.header.manual',
                A: 'messages.history.stopReasons.alarm',
                I: 'messages.history.installStatus',
                G: 'messages.history.programStatus',
                Z: 'messages.history.powerStatus',
                C: 'messages.history.clockSet',
                W: 'messages.history.NWWStatus',
                '2': 'messages.history.twoWireStatus',
                O: 'messages.history.bufferOverrun'
              })
            }
          },
          value3: {
            label: this.$t('messages.history.value3'),
              valueConversion: this.valueOrDash
          },
          c2: {
            label: this.$t('messages.history.value2'),
            valueConversion: (value) => {
              if(value > 946684800) return moment.utc(value, 'X')
              if(value === '') return '-'

              return this.translationOrValue(value, {
                MI: 'messages.header.irroff',
                MM: 'messages.header.manual',
                MA: 'messages.header.auto',
                MP: 'messages.header.program',
                MS: 'messages.header.setup',
                MV: 'messages.header.advanced',
                A0: 'messages.sharedTexts.status.off',
                A1: 'messages.sharedTexts.status.on',
                A2: 'messages.sharedTexts.status.instantOnOff'
              })
            }
          }
        },
        moisture: {
          date: {
            label: this.$t('messages.sharedTexts.time.date'),
            valueConversion: this.dateFromEpoch
          },
          time: {
            label: this.$t('messages.settings.time'),
            valueConversion: this.timeFromEpoch
          },
          sensorID: {
            label: this.$t('messages.history.sensorID'),
            valueConversion: (value) => value !== '0' ? value : '-'
          },
          status: {
            label: this.$t('messages.sharedTexts.status.status'),
            valueConversion: (value) => {
              if(value === '') return '-'
                
              return this.translationOrValue(value, {
                '0': 'messages.sharedTexts.status.faulted',
                '1': 'messages.header.status.ok',
                '2': 'messages.history.stopReasons.alarm'
              })
            }
          },
          moisture: {
            label: this.$t('messages.moisture.title'),
          },
          rootZone: {
            label: this.$t('messages.history.rootZone'),
            valueConversion: (value) => value
            ? this.convertUnits(value, 'in', 'cm')
            : '-'
          },
          temperature: {
            label: this.$t('messages.history.temperature'),
            valueConversion: this.valueOrDash 
          },
          s1: {
            label: `${this.$tc('messages.sharedTexts.sensor', 1)} 1`,
            valueConversion: this.valueOrDash
          },
          s2: {
            label: `${this.$tc('messages.sharedTexts.sensor', 1)} 2`,
            valueConversion: this.valueOrDash
          },
          s3: {
            label: `${this.$tc('messages.sharedTexts.sensor', 1)} 3`,
            valueConversion: this.valueOrDash
          },
          s4: {
            label: `${this.$tc('messages.sharedTexts.sensor', 1)} 4`,
            valueConversion: this.valueOrDash
          },
          s5: {
            label: `${this.$tc('messages.sharedTexts.sensor', 1)} 5`,
            valueConversion: this.valueOrDash
          },
          s6: {
            label: `${this.$tc('messages.sharedTexts.sensor', 1)} 6`,
            valueConversion: this.valueOrDash
          },
          s7: {
            label: `${this.$tc('messages.sharedTexts.sensor', 1)} 7`,
            valueConversion: this.valueOrDash
          },
          s8: {
            label: `${this.$tc('messages.sharedTexts.sensor', 1)} 8`,
            valueConversion: this.valueOrDash
          },
          s9: {
            label: `${this.$tc('messages.sharedTexts.sensor', 1)} 9`,
            valueConversion: this.valueOrDash
          },
          s10: {
            label: `${this.$tc('messages.sharedTexts.sensor', 1)} 10`,
            valueConversion: this.valueOrDash
          },
          depth2: {
            label: this.depthToString(2),
            valueConversion: this.valueOrDash
          },
          depth4: {
            label: this.depthToString(4),
            valueConversion: this.valueOrDash
          },
          depth6: {
            label: this.depthToString(6),
            valueConversion: this.valueOrDash
          },
          depth8: {
            label: this.depthToString(8),
            valueConversion: this.valueOrDash
          },
          depth10: {
            label: this.depthToString(10),
            valueConversion: this.valueOrDash
          },
          depth12: {
            label: this.depthToString(12),
            valueConversion: this.valueOrDash
          },
          depth16: {
            label: this.depthToString(16),
            valueConversion: this.valueOrDash
          },
          depth20: {
            label: this.depthToString(20),
            valueConversion: this.valueOrDash
          },
          depth24: {
            label: this.depthToString(24),
            valueConversion: this.valueOrDash
          },
          depth28: {
            label: this.depthToString(28),
            valueConversion: this.valueOrDash
          },
          depth32: {
            label: this.depthToString(32),
            valueConversion: this.valueOrDash
          },
          depth36: {
            label: this.depthToString(36),
            valueConversion: this.valueOrDash
          },
          depth40: {
            label: this.depthToString(40),
            valueConversion: this.valueOrDash
          },
          depth44: {
            label: this.depthToString(44),
            valueConversion: this.valueOrDash
          },
          depth48: {
            label: this.depthToString(48),
            valueConversion: this.valueOrDash
          },
          depth52: {
            label: this.depthToString(52),
            valueConversion: this.valueOrDash
          },
          depth56: {
            label: this.depthToString(56),
            valueConversion: this.valueOrDash
          },
          depth60: {
            label: this.depthToString(60),
            valueConversion: this.valueOrDash
          }
        },
        rawInformation: {
          date: {
            label: this.$t('messages.sharedTexts.time.date'),
            valueConversion: this.dateFromEpoch
          },
          time: {
            label: this.$t('messages.settings.time'),
            valueConversion: this.timeFromEpoch
          },
          eventType: {
            label: this.$t('messages.history.eventType'),
            valueConversion: (value) => this.translationOrValue(value, {
              P: 'messages.sharedTexts.program',
              D: 'messages.sharedTexts.station',
              U: 'messages.history.waterUsage',
              H: 'messages.history.waterHour',
              M: 'messages.history.stopReasons.modeChange',
              A: 'messages.history.stopReasons.alarm',
              I: 'messages.history.installStatus',
              G: 'messages.history.programChange',
              Z: 'messages.history.powerStatus',
              C: 'messages.history.clockSet',
              W: 'messages.history.NWWStatus',
              E: 'messages.history.etCount',
              T: 'messages.history.etcCount',
              R: 'messages.history.rainCount',
              2: 'messages.history.twoWireStatus',
              O: 'messages.history.bufferOverrun',
            })
          },
          program: {
            label: this.$tc('messages.sharedTexts.program', 1),
            valueConversion: this.valueOrDash
          },
          station: {
            label: this.$tc('messages.sharedTexts.station', 1),
            valueConversion: (value) => {
              if(value === '') return '-'
              
              return value > 10000
              ? { 10001: 'MV', 11001: 'Boo1', 11002: 'Boo2', 12001: 'COV'}[value]
              : value
            }
          },
          action: {
            label: this.$t('messages.sharedTexts.controls.action'),
            valueConversion: (value) => this.translationOrValue(value, {
              0: 'messages.sharedTexts.controls.stop',
              1: 'messages.sharedTexts.controls.start',
              2: 'messages.history.statusTypes.pausing',
              3: 'messages.history.statusTypes.paused',
              4: 'messages.sharedTexts.controls.resume',
              5: 'messages.history.statusTypes.waiting',
              6: 'messages.history.statusTypes.soaking',
            })
          },
          status: {
            label: this.$t('messages.sharedTexts.status.status'),
            valueConversion: (value) => this.translationOrValue(value, {
              0: 'messages.sharedTexts.status.ok',
              1: 'messages.history.stopReasons.fail',
              2: 'messages.history.stopReasons.stoppedByLocalOperator',
              3: 'messages.history.stopReasons.stoppedByRemoteOperator',
              4: 'messages.history.stopReasons.maxPrograms',
              5: 'messages.history.stopReasons.maxStations',
              6: 'messages.history.stopReasons.nonWW',
              7: 'messages.history.stopReasons.alarm',
              8: 'messages.history.stopReasons.modeChange',
              9: 'messages.history.stopReasons.cyclesNotEnded',
            })
          },
          operation: {
            label: this.$t('messages.history.operation'),
            valueConversion: (value) => this.translationOrValue(value, {
              A: 'messages.header.auto',
              M: 'messages.header.manual',
            })
          },
          oldMode: {
            label: this.$t('messages.sharedTexts.status.oldMode'),
            valueConversion: (value) => this.translationOrValue(value, {
              A: 'messages.header.auto',
              M: 'messages.header.manual',
              I: 'messages.header.irroff',
              P: 'messages.header.program',
              S: 'messages.header.setup',
              V: 'messages.header.advanced',
            })
          },
          mode: {
            label: this.$t('messages.sharedTexts.status.mode'),
            valueConversion: (value) => this.translationOrValue(value, {
              A: 'messages.header.auto',
              M: 'messages.header.manual',
              I: 'messages.header.irroff',
              P: 'messages.header.program',
              S: 'messages.header.setup',
              V: 'messages.header.advanced',
            })
          },
          method: {
            label: this.$t('messages.sharedTexts.method'),
            valueConversion: (value) => this.translationOrValue(value, {
              0: 'messages.programEdit.wb.title',
              1: 'messages.programEdit.et.title',
            })
          },
          value: {
            label: this.$t('messages.sharedTexts.value')
          },
          cycle: {
            label: this.$tc('messages.sharedTexts.cycle', 1)
          },
          cycles: {
            label: this.$tc('messages.sharedTexts.cycle', 0)
          },
          unscheduledHour: {
            label: this.$t('messages.history.unscheduledHour'),
            valueConversion: this.convertGal
          },
          expectedHour: {
            label: this.$t('messages.history.expectedHour'),
            valueConversion: this.convertGal
          },
          waterHour: {
            label: this.$t('messages.history.waterHour'),
            valueConversion: this.convertGal
          },
          unscheduledDay: {
            label: this.$t('messages.history.unscheduledDay'),
            valueConversion: this.convertGal
          },
          expectedDay: {
            label: this.$t('messages.history.expectedDay'),
            valueConversion: this.convertGal
          },
          waterDay: {
            label: this.$t('messages.history.waterDay'),
            valueConversion: this.convertGal
          },
          etDay: {
            label: this.$t('messages.history.etDay'),
            valueConversion: this.convertGal
          },
          rainDayForETc: {
            label: this.$t('messages.history.rainDayForETc'),
            valueConversion: this.convertGal
          },
          rainHour: {
            label: this.$t('messages.history.rainHour'),
            valueConversion: this.convertGal
          },
          rainDiscard: {
            label: this.$t('messages.history.rainDiscard'),
            valueConversion: this.convertGal
          },
          rainExcess: {
            label: this.$t('messages.history.rainExcess'),
            valueConversion: this.convertGal
          },
          oldTime: {
            label: this.$t('messages.history.oldTime'),
            valueConversion: this.timeFromEpoch
          },
          newTime: {
            label: this.$t('messages.history.newTime'),
            valueConversion: this.timeFromEpoch
          },
          active: {
            label: this.$t('messages.sharedTexts.status.active'),
            valueConversion: this.valueOrDash
          },
          current: {
            label: this.$t('messages.sharedTexts.current'),
            valueConversion: this.valueOrDash
          },
          voltage: {
            label: this.$t('messages.sharedTexts.voltage'),
            valueConversion: this.valueOrDash
          },
          where: {
            label: this.$t('messages.sharedTexts.where'),
            valueConversion: (value) => this.translationOrValue(value, {
              0: 'messages.history.local',
              1: 'messages.history.remote',
            })
          },
          alarm: {
            label: this.$t('messages.sharedTexts.status.status'),
            valueConversion: (value) => this.translationOrValue(value, {
              1: 'messages.sharedTexts.alarms.1',
              2: 'messages.sharedTexts.alarms.2',
              3: 'messages.sharedTexts.alarms.3',
              4: 'messages.sharedTexts.alarms.4',
              5: 'messages.sharedTexts.alarms.5',
              6: 'messages.sharedTexts.alarms.6',
              7: 'messages.sharedTexts.alarms.7',
              8: 'messages.sharedTexts.alarms.8',
              9: 'messages.sharedTexts.alarms.9',
              10: 'messages.sharedTexts.alarms.10',
              11: 'messages.sharedTexts.alarms.11',
              12: 'messages.sharedTexts.alarms.12',
            })
          },
          state: {
            label: this.$t('messages.deviceList.state'),
            valueConversion: (value) => this.translationOrValue(value, {
              0: 'messages.history.on',
              1: 'messages.history.off',
              2: 'messages.sharedTexts.status.instantOnOff',
            })
          },
          hour: {
            label: this.$tc('messages.sharedTexts.time.hour', 1),
            valueConversion: this.valueOrDash
          },
          activeStations: {
            label: this.$t('messages.history.activeStations'),
            valueConversion: this.valueOrDash
          },
        },
        all: {
          date: {
            label: this.$t('messages.sharedTexts.time.date'),
            valueConversion: this.dateFromEpoch
          },
          time: {
            label: this.$t('messages.settings.time'),
            valueConversion: this.timeFromEpoch
          },
          eventType: {
            label: this.$t('messages.history.eventType'),
            valueConversion: (value) => this.translationOrValue(value, {
              P: 'messages.sharedTexts.program',
              D: 'messages.sharedTexts.station',
              U: 'messages.history.waterUsage',
              H: 'messages.history.waterHour',
              M: 'messages.history.stopReasons.modeChange',
              A: 'messages.history.stopReasons.alarm',
              I: 'messages.history.installStatus',
              G: 'messages.history.programChange',
              Z: 'messages.history.powerStatus',
              C: 'messages.history.clockSet',
              W: 'messages.history.NWWStatus',
              E: 'messages.history.etCount',
              T: 'messages.history.etcCount',
              R: 'messages.history.rainCount',
              2: 'messages.history.twoWireStatus',
              O: 'messages.history.bufferOverrun',
            })
          },
          program: {
            label: this.$tc('messages.sharedTexts.program', 1),
            valueConversion: this.valueOrDash
          },
          station: {
            label: this.$tc('messages.sharedTexts.station', 1),
            valueConversion: (value) => {
              if(value === '') return '-'
              
              return value > 10000
              ? { 10001: 'MV', 11001: 'Boo1', 11002: 'Boo2', 12001: 'COV'}[value]
              : value
            }
          },
          action: {
            label: this.$t('messages.sharedTexts.controls.action'),
            valueConversion: (value) => this.translationOrValue(value, {
              0: 'messages.sharedTexts.controls.stop',
              1: 'messages.sharedTexts.controls.start',
              2: 'messages.history.statusTypes.pausing',
              3: 'messages.history.statusTypes.paused',
              4: 'messages.sharedTexts.controls.resume',
              5: 'messages.history.statusTypes.waiting',
              6: 'messages.history.statusTypes.soaking',
            })
          },
          status: {
            label: this.$t('messages.sharedTexts.status.status'),
            valueConversion: (value) => this.translationOrValue(value, {
              0: 'messages.sharedTexts.status.ok',
              1: 'messages.history.stopReasons.fail',
              2: 'messages.history.stopReasons.stoppedByLocalOperator',
              3: 'messages.history.stopReasons.stoppedByRemoteOperator',
              4: 'messages.history.stopReasons.maxPrograms',
              5: 'messages.history.stopReasons.maxStations',
              6: 'messages.history.stopReasons.nonWW',
              7: 'messages.history.stopReasons.alarm',
              8: 'messages.history.stopReasons.modeChange',
              9: 'messages.history.stopReasons.cyclesNotEnded',
            })
          },
          operation: {
            label: this.$t('messages.history.operation'),
            valueConversion: (value) => this.translationOrValue(value, {
              A: 'messages.header.auto',
              M: 'messages.header.manual',
            })
          },
          oldMode: {
            label: this.$t('messages.sharedTexts.status.oldMode'),
            valueConversion: (value) => this.translationOrValue(value, {
              A: 'messages.header.auto',
              M: 'messages.header.manual',
              I: 'messages.header.irroff',
              P: 'messages.header.program',
              S: 'messages.header.setup',
              V: 'messages.header.advanced',
            })
          },
          mode: {
            label: this.$t('messages.sharedTexts.status.mode'),
            valueConversion: (value) => this.translationOrValue(value, {
              A: 'messages.header.auto',
              M: 'messages.header.manual',
              I: 'messages.header.irroff',
              P: 'messages.header.program',
              S: 'messages.header.setup',
              V: 'messages.header.advanced',
            })
          },
          method: {
            label: this.$t('messages.sharedTexts.method'),
            valueConversion: (value) => this.translationOrValue(value, {
              0: 'messages.programEdit.wb.title',
              1: 'messages.programEdit.et.title',
            })
          },
          value: {
            label: this.$t('messages.sharedTexts.value')
          },
          cycle: {
            label: this.$tc('messages.sharedTexts.cycle', 1)
          },
          cycles: {
            label: this.$tc('messages.sharedTexts.cycle', 0)
          },
          unscheduledHour: {
            label: this.$t('messages.history.unscheduledHour'),
            valueConversion: this.convertGal
          },
          expectedHour: {
            label: this.$t('messages.history.expectedHour'),
            valueConversion: this.convertGal
          },
          waterHour: {
            label: this.$t('messages.history.waterHour'),
            valueConversion: this.convertGal
          },
          unscheduledDay: {
            label: this.$t('messages.history.unscheduledDay'),
            valueConversion: this.convertGal
          },
          expectedDay: {
            label: this.$t('messages.history.expectedDay'),
            valueConversion: this.convertGal
          },
          waterDay: {
            label: this.$t('messages.history.waterDay'),
            valueConversion: this.convertGal
          },
          etDay: {
            label: this.$t('messages.history.etDay'),
            valueConversion: this.convertGal
          },
          rainDayForETc: {
            label: this.$t('messages.history.rainDayForETc'),
            valueConversion: this.convertGal
          },
          rainHour: {
            label: this.$t('messages.history.rainHour'),
            valueConversion: this.convertGal
          },
          rainDiscard: {
            label: this.$t('messages.history.rainDiscard'),
            valueConversion: this.convertGal
          },
          rainExcess: {
            label: this.$t('messages.history.rainExcess'),
            valueConversion: this.convertGal
          },
          oldTime: {
            label: this.$t('messages.history.oldTime'),
            valueConversion: this.timeFromEpoch
          },
          newTime: {
            label: this.$t('messages.history.newTime'),
            valueConversion: this.timeFromEpoch
          },
          active: {
            label: this.$t('messages.sharedTexts.status.active'),
            valueConversion: this.valueOrDash
          },
          current: {
            label: this.$t('messages.sharedTexts.current'),
            valueConversion: this.valueOrDash
          },
          voltage: {
            label: this.$t('messages.sharedTexts.voltage'),
            valueConversion: this.valueOrDash
          },
          where: {
            label: this.$t('messages.sharedTexts.where'),
            valueConversion: (value) => this.translationOrValue(value, {
              0: 'messages.history.local',
              1: 'messages.history.remote',
            })
          },
          alarm: {
            label: this.$t('messages.sharedTexts.status.status'),
            valueConversion: (value) => this.translationOrValue(value, {
              1: 'messages.sharedTexts.alarms.1',
              2: 'messages.sharedTexts.alarms.2',
              3: 'messages.sharedTexts.alarms.3',
              4: 'messages.sharedTexts.alarms.4',
              5: 'messages.sharedTexts.alarms.5',
              6: 'messages.sharedTexts.alarms.6',
              7: 'messages.sharedTexts.alarms.7',
              8: 'messages.sharedTexts.alarms.8',
              9: 'messages.sharedTexts.alarms.9',
              10: 'messages.sharedTexts.alarms.10',
              11: 'messages.sharedTexts.alarms.11',
              12: 'messages.sharedTexts.alarms.12',
            })
          },
          state: {
            label: this.$t('messages.deviceList.state'),
            valueConversion: (value) => this.translationOrValue(value, {
              0: 'messages.history.on',
              1: 'messages.history.off',
              2: 'messages.sharedTexts.status.instantOnOff',
            })
          },
          hour: {
            label: this.$tc('messages.sharedTexts.time.hour', 1),
            valueConversion: this.valueOrDash
          },
          activeStations: {
            label: this.$t('messages.history.activeStations'),
            valueConversion: this.valueOrDash
          },
        }        
      }
    }
  },
  computed: {
    computedGroup() {
      //  wtf Thomas?  "capabilities.steps.maxStationsPerStep" doesn't even exist in this context -jimm 09.04.2021
      // old line -> return this.store.getData('capabilities.steps.maxStationsPerStep') !== 1 ? 'twc' : 'rkx'
      return this.store.getData('controller.group').toLowerCase() === 'twc' ? 'twc' : 'rkx'
    },
    computedSelectedItem() {
      return this.navigation.find(item => item.value === this.selectedItem)
    },
    computedTitle() {
      return this.computedSelectedItem ? this.computedSelectedItem.label : null
    }
  },
  watch: {
    optionSelected(option) {
      if(this.selectedItem && (option === undefined || option))
        this.requestData()
    },
    selectedItem(item) {
      const navigationItem = this.computedSelectedItem

      // if we got options then use them, otherwise use the main label:value as /option which makes a fresh requestData() call
      // fixes bad design(?) in not clearing out /option when value is same as last time through (such as null for history pages without options)
      // -jimm 09/04/2021 
      if(navigationItem) this.optionSelected = 
        navigationItem.options[0] ? navigationItem.options[0] : { "label": navigationItem.label, "value": navigationItem.value }
    }
  },
  created() {
    this.sendMicroServiceRequest({
      method: 'GET',
      url: `api/history?group=${this.computedGroup}`,
      timeout: 10000
    })
    .then((response) => {
      if(!(response && response.data)) return console.error('No data')

      const tableKeys = Object.keys(this.formatTables)
      const responseKeys = response.data.map(item => item.value)

      console.log('<-- History MSR: ' + JSON.stringify(response, null, 2))

      response.data.length === responseKeys.length && responseKeys.every((item, index) => responseKeys.includes(item))
      ? this.navigation = response.data
      : console.error('History endpoint contains fields formatTable doesn\'t have.')
    })
  },
  methods: {
    sleep(ms) {  // using this pseudo-sleep() to wait until the ipLookUp completes
      return new Promise(
        resolve => setTimeout(resolve, ms)
      )
    },
    ipLookUp() {
      // TODO improve this by adding fallback api locations incase one fails.  *BUT* that may require changing call to an async await() since retires may take longer than the sleep(2000)
      let whereUser = []
      $.getJSON("https://api.ipify.org?format=json", 
        function(data) { 
          // data.ip = "134.201.250.155"  // Washington, DC (for testing) -jimm 24.feb.2021
          $.ajax('http://api.ipstack.com/' + data.ip + '?access_key=f475abdb03eb312c72633ce81a497807')
          .then(
            function success(response) {
              console.log('Success!')
              whereUser.push({'country_code': response.country_code, 'contintent_code': response.continent_code})
              // console.log(JSON.stringify(whereUser, null, 2))
            },

            function fail(data, status) {
              console.log('Request failed.  Returned status of', status)
            }
          )
        }
      ) 
      return whereUser
    },
    currentTableToCSV() {
      const date = new Date()

      if(navigator.language) {
        console.log('i haz navigator.language: ' + navigator.language)
      }
      // let whereUser = this.ipLookUp()
      let delimiter = ""
      let quotes = "\""

      // -- fancy ipLookup version is disabled for now.  relying on browser navigator.language object (see above)
      // sleep() used to wait for ipLookup to finish - short-term HACK!
      // TODO possible improve(?) by using async await() ?  -jimm 24.feb.2021
      // see https://www.sitepoint.com/delay-sleep-pause-wait/ for reference
      // this.sleep(2000)
      //   .then(() => {
      //     console.log('after iplookup()')
      //     console.log(JSON.stringify(whereUser[0].country_code, null, 2))
    
          // let country = whereUser[0].country_code

      // for now lets use navigator until someone wants more precise location/region detection
          let country = navigator.language.slice(-2)
          switch(country) {
            case "DK":
            case "DE":
            case "NL":
            case "RO":
            case "IT":
              console.log('i haz ' + country)
              delimiter = ";"
              break
            default:
              console.log('i haz default (US, UK, JP, AU, etc.)')
              delimiter = ","
              break
          }
   
          console.log('delimiter: ' + delimiter + ' / quotes: ' + quotes)
    
          const csvData = this.$refs.grid.getTableAsCSV(delimiter, quotes)
    
          const link = document.createElement('a')
          link.id = 'download-csv'
          link.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(csvData))
          link.setAttribute('download', `${date.toDateString()} ${this.store.getData('controller.name')} History.csv`)
    
          document.body.appendChild(link)
    
          const btn = document.querySelector('#download-csv')
          btn.click()
          setTimeout(() => btn.remove(), 0)

        // }   // these go with the ipLookup version of the 'determine locale' code

      // )

    },
    requestData() {
      this.sendRequest(`api/auth/history/${this.selectedItem}`, 'list')
    },
    valueOrDash: (value) => value === '-' || value === '' || value === '-1' ? '-' : Number(value).toLocaleString(),
    convertGal(value) {
      // console.log('convertGal(value), value -> ' + value)
      if(value === '-1' || value == '-0.01' || value === '') return '-'

      let val = parseInt(value)
      val = !isNaN(val) ? this.convertUnits(val, 'gal', 'l') : '-'

      return Number(val).toLocaleString()
    },

    convertGalFloat(value) {
      if(value === '-1' || value === '-0.01' || value === '') return '-'

      let val = parseFloat(value)
      val = !isNaN(val) ? this.convertUnits(val, 'gal', 'l') : '-'

      return Number(val).toLocaleString()
    },

    translationOrValue(value, list) {
      if(value === '' || value === '-1') return '-'

      const match = list[value]
      return match ?  this.$tc(match, 1) : value
    },
    depthToString(num) {
      const denominator = this.getDenominator('centimetre', 'inch', 0)
      const value = this.convertUnits(num, 'in', 'cm')
      return `${Number(value).toLocaleString()}${denominator}`
    },
    secondsToTimeString(runtime) {
      runtime = secondsToObj(runtime)
      
      const hours = (runtime.hh !== 0) ? runtime.hh + this.$tc('messages.sharedTexts.time.h', 1) : ''
      const minutes = (runtime.mm !== 0) ? runtime.mm + this.$tc('messages.sharedTexts.time.m', 1) : ''
      const seconds = (runtime.ss !== 0) ? runtime.ss + this.$tc('messages.sharedTexts.time.s', 1) : ''
      return runtime = `${hours} ${minutes} ${seconds}`
    },
    dateFromEpoch(epoch) {
      epoch = parseInt(epoch, 10) * 1000
      if(isNaN(epoch)) return '-'
      const format = this.store.getData('settings.time.use12HourClock') ? 'MM/DD/YYYY' : 'DD/MM/YYYY'
      return moment(epoch).format(format)
    },
    timeFromEpoch(epoch) {
      epoch = parseInt(epoch, 10) * 1000
      if(isNaN(epoch)) return '-'
      const format = this.store.getData('settings.time.currentFormat').split(',')[1]
      return moment(epoch).utc().format(format)
    },
    sendRequest(path, pointerString) {
      this.list = null
      
      let option = this.optionSelected ? this.optionSelected.value : null
      const oldPath = this.selectedItem + option

      option = `/${option}`

      this.sendMicroServiceRequest({
        method: 'GET',
        url: `${path}${option}?group=${this.computedGroup}`,
        timeout: 75000
      })
      .then((response) => {
        // Check if requested data matches what we asked for
        const newPath = this.selectedItem + (this.optionSelected ? this.optionSelected.value : null)
        const sameOptions = oldPath === newPath

        if(!(option === null || sameOptions)) return // reject old data

        if(response && response.data)
          this.$set(this, pointerString, response.data)

        let response_output = JSON.stringify(response).length > 100 ? JSON.stringify(response).substr(0, 100) + '&hellip;' : JSON.stringify(response)
        console.log('<-- History MSR: ' + response_output)

      })
    },
    navigationSelectedItem(item) {
      this.selectedItem = item
    }
  }
}
</script>

<style lang="scss">
.history {
  $height: calc(100vh - 2 * #{$MenuHeight});
  $padding: 10px;

  box-sizing: border-box;
  overflow-y: scroll;
  
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 48.333px 1fr;

  height: $height;
  max-height: $height;
  margin-right: 70px;
 
  box-shadow: 0 0 5px $color-white;
  background-color: $color-white;
  
  .navigation {
    &__tabs {
      text-align: center;
      &__tab{
        display: inline-block;
        &--compressor {
          display: none !important;
        }
        &__label {
          display: none !important;
        }
      }
    }
  }

  &__header {
    overflow: hidden;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    min-height: 48.333px;
    margin-top: 15px;
    margin-right: 5px;

    .g-btn--blue {
      margin: 10px 5px !important;
    }

    > * {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &__option-buttons {
      display: flex;
      align-items: center;
      flex-direction: row-reverse;

      li {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
        width: 100%;
      }

      &__refresh {
        color: $color-blue-marine;
      }
    }
  }

  &__body {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto minmax(0, 1fr);
  }
}

@media screen and (min-width: $bp-mobile) {
  .history {
    margin-right: 100px;
    overflow: hidden;
    grid-template-columns: auto 1fr;
    grid-template-rows: minmax(0, 1fr);

    &__header {
      > * {
        justify-content: right;
      }

      &__title {
        display: none;
      }

      &__option-buttons {
        li {
          width: auto;
          display: inline-block;
        }
      }
    }

    .navigation {
      &__tabs {
        text-align: left;
        &__tab{
          display: grid;
          &--compressor {
            display: grid !important;
          }
          &__label {
            display: inline-block !important;
          }
        }
      }
    }
  }
}
</style>
